import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import "../../../assets/css/page/TestAgent.scss";
import { shortenString } from "../../../Constant/helper";
import EmptyState from "../Common/EmptyState";
import SearchBar from "../Common/Search/SearchBar";
import { hasCampaignsAccess, isEmpty, rgba } from "../CommonComponents/Common";
import CustomScrollbars from "../CommonComponents/CustomScrollbars";
import PageLoader from "../CommonComponents/PageLoader";
import ModelSelector from "../ProjectSettings/ModelSelector";

const getInitials = (name) => {
  return (
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?"
  );
};

const TestAgentBody = ({ testAgentObj }) => {
  const theme: any = useTheme();
  const brandColor = theme?.custom?.brandColor;
  const lightBrandColor = rgba(brandColor, 0.08);

  const reducer: any = useSelector((state) => state);
  const {
    AccountReducer: { constantData, accountRoles },
  } = reducer;
  const { state, updateState, model, setModelState, nameData } = testAgentObj;
  const { filteredCustomerProfiles, selectedCases, dataLoading, searchText } =
    state;
  const [modelOptions, setModelOptions] = useState([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverData, setPopoverData] = useState(null);
  const closeTimerRef: any = useRef(null);

  useEffect(() => {
    setModelOptions(constantData?.modelOptions || []);
  }, [constantData]);

  const findName = (category, key) => {
    const hasNameDataKey = !isEmpty(nameData) && nameData?.[category]?.[key];
    const hasAccess = nameData?.portalLevel || hasCampaignsAccess(accountRoles);

    if (hasAccess && hasNameDataKey) return nameData[category][key];
    return key;
  };

  const handleSelectCase = (test) => {
    updateState({
      selectedCases: selectedCases.includes(test)
        ? selectedCases.filter((t) => t !== test)
        : [...selectedCases, test],
    });
  };

  const handleOpenPopover = (event, data) => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setAnchorEl(event.currentTarget);
    setPopoverData(data);
  };

  const handlePopoverCloseImmediate = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setAnchorEl(null);
    setPopoverData(null);
  };

  if (dataLoading) return <PageLoader height="70vh" />;

  return (
    <Box className="px-2">
      <Box className="flex-between gap-2 mb-2">
        <Box className="flex-column gap-1">
          <Typography className="font-16-600-grey">
            Customer Profiles
          </Typography>
          <Typography variant="body2" sx={{ color: "#666" }}>
            Select profiles to test your agent against different scenarios
          </Typography>
        </Box>
        <Box className="flex-between gap-2">
          <Box className="flex-row flex-xy-center gap-2" sx={{ minWidth: 200 }}>
            <ModelSelector
              model={model}
              modelOptions={modelOptions}
              setState={setModelState}
              findName={findName}
            />
          </Box>
          <SearchBar
            searchText={searchText}
            placeholder="Search Customers..."
            handleChange={(e) => {
              e.persist();
              updateState({ searchText: e.target.value });
            }}
          />
        </Box>
      </Box>
      <Box className="mt-3">
        {filteredCustomerProfiles?.length > 0 ? (
          <>
            <CustomScrollbars height="45vh">
              <Grid container spacing={2}>
                {filteredCustomerProfiles?.map((test, index) => {
                  const isSelected = selectedCases.includes(test);

                  return (
                    <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card
                        sx={{
                          cursor: "pointer",
                          borderRadius: 3,
                          border: "1px solid",
                          borderColor: isSelected ? brandColor : "#e0e0e0",
                          backgroundColor: isSelected
                            ? lightBrandColor
                            : "white",
                          transition: "all 0.25s ease",
                          position: "relative",
                          height: "100%",
                          "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: `0 8px 18px ${rgba(brandColor, 0.16)}`,
                            borderColor: brandColor,
                          },
                        }}
                        onClick={() => handleSelectCase(test)}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box className="flex-align-center gap-2 mb-2">
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                background: `linear-gradient(135deg, ${brandColor} 0%, ${rgba(
                                  brandColor,
                                  0.9
                                )} 100%)`,
                                fontWeight: 600,
                                fontSize: "1.1rem",
                              }}
                            >
                              {getInitials(test?.customerName)}
                            </Avatar>

                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1a1a1a",
                                  fontSize: "0.95rem",
                                  textTransform: "capitalize",
                                }}
                              >
                                {test?.customerName || ""}
                              </Typography>
                              <Chip
                                label={shortenString(
                                  test?.shortDescription,
                                  25
                                )}
                                size="small"
                                sx={{
                                  backgroundColor: isSelected
                                    ? brandColor
                                    : lightBrandColor,
                                  color: isSelected ? "white" : brandColor,
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                  height: "auto",
                                  py: 0.5,
                                }}
                              />
                            </Box>

                            <Box
                              sx={{
                                position: "absolute",
                                top: 16,
                                right: 16,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPopover(e, test?.userFields || {});
                                }}
                                aria-label="show user fields"
                                sx={{
                                  border: `1px solid ${rgba(brandColor, 0.12)}`,
                                }}
                                title="Show user fields"
                              >
                                <InfoOutlinedIcon
                                  sx={{ fontSize: 18, color: brandColor }}
                                />
                              </IconButton>
                            </Box>
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              color: "#555",
                              lineHeight: 1.6,
                              mt: 1,
                            }}
                          >
                            {shortenString(test?.customerScenario, 70)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </CustomScrollbars>
          </>
        ) : (
          <EmptyState message="No Customer Profiles Found" />
        )}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverCloseImmediate}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: { p: 1.5, minWidth: 220, borderRadius: 2 },
        }}
      >
        <Box className="p-2">
          <Typography sx={{ fontWeight: 600, mb: 1 }}>User fields</Typography>
          {popoverData && Object.keys(popoverData).length > 0 ? (
            Object.entries(popoverData).map(([key, value]) => (
              <Box key={key} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                <Typography
                  sx={{
                    color: "#777",
                    minWidth: 90,
                    fontSize: 14,
                  }}
                >
                  {key}:
                </Typography>
                <Typography sx={{ fontSize: 13 }}>{String(value)}</Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ color: "#777", fontSize: 13 }}>
              No additional info
            </Typography>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default TestAgentBody;
